import { toBlob, toCanvas, getFontEmbedCSS } from 'html-to-image';

// Shared export helpers for every poster editor (rate chart, biggapon …): capture the un-zoomed page nodes to images,
// download, make a PDF, print, and read a logo the user picked. Nothing here knows what the pages show.

// ── small inputs ──────────────────────────────────────────────────────────
const LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
export const LOGO_MAX_BYTES = 3 * 1024 * 1024;
const LOGO_MAX_PX = 512;

/**
 * Validates a picked logo (type allowlist + size cap) and shrinks it to ≤512px as a data URL.
 * Transparency is kept on purpose — optimizeImage re-encodes to JPEG, which would turn a transparent logo black.
 * Throws Error('type') / Error('size') / Error('decode').
 */
export async function readLogoFile(file) {
    if (!file || !LOGO_TYPES.has(file.type)) throw new Error('type');
    if (file.size > LOGO_MAX_BYTES) throw new Error('size');
    let bitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        throw new Error('decode');
    }
    const scale = Math.min(1, LOGO_MAX_PX / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    return canvas.toDataURL(file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png', 0.92);
}

/** A file-name stem from a label (e.g. the shop name): keeps Bangla letters, drops what file systems reject. */
export function fileStem(label, prefix = 'poster') {
    const s = String(label || '').replace(/[\\/:*?"<>|\u0000-\u001f]/g, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    return s ? `${prefix}-${s}` : prefix;
}

// ── capture ───────────────────────────────────────────────────────────────
/**
 * Renders each page node to an image Blob. html-to-image (not html2canvas) so the browser's own text engine shapes
 * the Bangla conjuncts. `nodes` must be the UN-scaled pages (the hidden export copy), never the zoomed preview.
 *   format 'png'  → crisp          format 'jpeg' → smaller (used for the PDF, printing and photo-like posters)
 * Fonts and images must be ready before this is called — the caller owns that.
 */
export async function capturePages(nodes, { format = 'png', pixelRatio = 3 } = {}) {
    let fontEmbedCSS = '';
    try { fontEmbedCSS = await getFontEmbedCSS(nodes[0]); } catch { /* the capture embeds fonts itself then */ }
    const opts = { pixelRatio, backgroundColor: '#ffffff', cacheBust: false, ...(fontEmbedCSS ? { fontEmbedCSS } : {}) };

    const blobs = [];
    for (const node of nodes) {
        if (format === 'jpeg') {
            const canvas = await toCanvas(node, opts);
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
            if (!blob) throw new Error('capture failed');
            blobs.push(blob);
        } else {
            const blob = await toBlob(node, opts);
            if (!blob) throw new Error('capture failed');
            blobs.push(blob);
        }
    }
    return blobs;
}

export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('read failed'));
    fr.readAsDataURL(blob);
});

/** Where a page image of aspect `ratio` (width ÷ height) sits on a real sheet: fitted inside it, centred. */
function fitOnSheet(pw, ph, ratio) {
    let w = pw;
    let h = pw / ratio;
    if (h > ph) { h = ph; w = ph * ratio; }
    return { w, h, x: (pw - w) / 2, y: (ph - h) / 2 };
}

/** JPEG page images → one PDF, one page per image, on a sheet of `sheetMm` = [width, height] (portrait or landscape). */
export async function pagesToPdf(jpegBlobs, sheetMm) {
    const { jsPDF } = await import('jspdf');
    const [pw, ph] = sheetMm;
    const orientation = pw > ph ? 'landscape' : 'portrait';
    const format = [Math.min(pw, ph), Math.max(pw, ph)]; // jsPDF takes the sheet portrait and swaps it for 'landscape'
    const doc = new jsPDF({ unit: 'mm', format, orientation, compress: true });
    for (let i = 0; i < jpegBlobs.length; i += 1) {
        const bitmap = await createImageBitmap(jpegBlobs[i]);
        const { w, h, x, y } = fitOnSheet(pw, ph, bitmap.width / bitmap.height);
        bitmap.close?.();
        if (i > 0) doc.addPage(format, orientation);
        doc.addImage(await blobToDataUrl(jpegBlobs[i]), 'JPEG', x, y, w, h, undefined, 'FAST');
    }
    return doc.output('blob');
}

/**
 * Sends the page images to the browser's print dialog through a throwaway hidden iframe (its own document, so no app CSS or
 * @page rule leaks in). The iframe is a blob: URL — a srcdoc one prints blank in some browsers.
 */
export function printPages(imageBlobs, sheetMm, title = 'Poster') {
    const [pw, ph] = sheetMm;
    const urls = imageBlobs.map((b) => URL.createObjectURL(b));
    const safeTitle = String(title).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); // becomes the default "Save as PDF" name
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title><style>
@page { size: ${pw}mm ${ph}mm; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; }
.pg { width: ${pw}mm; height: ${ph}mm; display: flex; align-items: center; justify-content: center; overflow: hidden; break-after: page; page-break-after: always; }
.pg:last-child { break-after: auto; page-break-after: auto; }
.pg img { max-width: 100%; max-height: 100%; display: block; }
</style></head><body>${urls.map((u) => `<div class="pg"><img src="${u}" alt=""></div>`).join('')}</body></html>`;
    const htmlUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    const cleanup = () => {
        try { document.body.removeChild(iframe); } catch { /* already gone */ }
        URL.revokeObjectURL(htmlUrl);
        urls.forEach((u) => URL.revokeObjectURL(u));
    };
    iframe.onload = async () => {
        const win = iframe.contentWindow;
        try {
            await Promise.all(Array.from(win.document.images).map((img) => img.decode().catch(() => {})));
            win.addEventListener('afterprint', () => setTimeout(cleanup, 500));
            win.focus();
            win.print();
        } catch { cleanup(); return; }
        setTimeout(cleanup, 120000); // safety net when the browser never fires afterprint
    };
    iframe.src = htmlUrl;
    document.body.appendChild(iframe);
}
